import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useMode } from "../context/ModeContext";
import { usePickerText } from "../context/ContentContext";
import { MODES, MODE_META } from "../lib/modes";

const SKIP_MODE = MODES[Math.floor(MODES.length / 2)]; // "Skip for now" lands on the middle option (Blocks)

const Option = ({ meta, onPick, testId, delay }) => {
  const Icon = meta.icon;
  const art = meta.art;
  return (
    // flex-col + items-start keeps the icon and text pinned to the top of every card (buttons centre content by default)
    <motion.button
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 220, damping: 24 }}
      whileHover={{ y: -4 }}
      onClick={onPick}
      className={`group relative isolate overflow-hidden flex flex-col items-start justify-start text-left rounded-3xl border-2 p-6 sm:p-8 bg-card transition-colors ${meta.accent} ${art ? "pb-44 sm:pb-52" : ""}`}
      data-testid={testId}
      data-art={art ? "true" : undefined}
    >
      {art && (
        <>
          {/* Mural fills the card; the overlay keeps the copy readable and lets the drill + treasure surface at the bottom */}
          <img src={art} alt="" aria-hidden="true" draggable={false} loading="eager"
            className="absolute inset-0 -z-20 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 dark:brightness-[0.7]" style={{ objectPosition: meta.artPos || "50% 50%" }} data-testid={`${testId}-art`} />
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-card/95 via-card/70 to-card/0" />
        </>
      )}
      {!art && (
        <div className={`inline-flex p-3 rounded-2xl bg-muted mb-4 group-hover:scale-110 transition-transform ${meta.text}`}>
          <Icon className="w-8 h-8" />
        </div>
      )}
      <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-1" data-testid={`${testId}-title`}>{meta.label}</h2>
      <p className="text-muted-foreground text-sm sm:text-base mb-4" data-testid={`${testId}-tagline`}>{meta.tagline}</p>
      <ul className="space-y-1.5 text-sm mb-6" data-testid={`${testId}-points`}>
        {meta.points.map((p) => (
          <li key={p} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-current shrink-0" />
            {p}
          </li>
        ))}
      </ul>
      <span className={`inline-flex items-center gap-1.5 font-semibold text-sm ${meta.text} ${art ? "rounded-full bg-card/85 backdrop-blur-sm px-3 py-1.5 -ml-3 shadow-sm" : ""}`}>
        Start with {meta.label} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </span>
    </motion.button>
  );
};

// "[label](https://url)" inside the eyebrow becomes a link (e.g. Creator Academy)
const renderLinks = (text) =>
  String(text || "").split(/(\[[^\]]+\]\([^)]+\))/g).filter(Boolean).map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    return m ? (
      <a key={i} href={m[2]} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-4 hover:text-foreground transition-colors" data-testid="mode-picker-eyebrow-link">{m[1]}</a>
    ) : <span key={i}>{part}</span>;
  });

export const ModePicker = () => {
  const { setMode } = useMode();
  const text = usePickerText();
  return (
    <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm overflow-y-auto" data-testid="mode-picker">
      <div className="min-h-full flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-5xl">
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2" data-testid="mode-picker-eyebrow">{renderLinks(text.eyebrow)}</p>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl" data-testid="mode-picker-heading">{text.heading}</h1>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base" data-testid="mode-picker-subheading">{text.subheading}</p>
          </motion.div>
          <div className="grid gap-4 md:grid-cols-3 items-stretch">
            {MODES.map((m, i) => (
              <Option key={m} meta={MODE_META[m]} onPick={() => setMode(m)} testId={`mode-picker-${m}`} delay={0.05 + i * 0.08} />
            ))}
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="mt-8 flex flex-col items-center gap-1 text-center">
            <button
              type="button"
              onClick={() => setMode(SKIP_MODE)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border px-5 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
              data-testid="mode-picker-skip-btn"
            >
              Skip for now <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-muted-foreground" data-testid="mode-picker-skip-note">
              Not sure? We'll start you with {MODE_META[SKIP_MODE].label} - you can switch in the top bar any time.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
